import React from 'react';
import { Content } from '.generated/Feature.CW.model';
import { Field, useSitecore } from '@sitecore-content-sdk/nextjs';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { withStandardComponentWrapper } from 'helpers/HOC';
import useIsEditing from 'lib/hooks/useIsEditing';

export type TextListProps = Content.TextListSettings_Component & {
  isSidenavPart?: boolean;
};

const isHtml = (str: string): boolean => {
  if (typeof str !== 'string') return false;
  // This regex checks for any HTML tag
  return /<\/?[a-z][\s\S]*>/i.test(str.trim());
};

const TextList: React.FC<TextListProps> = (props) => {
  const isEditing = useIsEditing();
  const { page } = useSitecore();
  const rawFields = page?.layout?.sitecore?.route?.fields ?? {};
  const sitecoreFieldKey = props?.fields?.sitecoreField?.value;

  if (!sitecoreFieldKey) {
    return null;
  }

  const rawValue =
    typeof sitecoreFieldKey === 'string'
      ? ((rawFields[sitecoreFieldKey] as Field<string> | undefined)?.value ?? '')
      : '';

  const isRichTextField = isHtml(rawValue);

  const textList = !isRichTextField
    ? rawValue
        .split(/[\r\n|]+/)
        .map((item: string) => item.trim())
        .filter((item: string) => item.length > 0)
    : [];

  if (
    !isEditing &&
    ((textList.length === 0 && !isRichTextField) || props?.fields?.subHeading?.value === '')
  ) {
    return <></>;
  }

  return (
    <section
      role="region"
      aria-labelledby={props?.fields?.subHeading?.value ? 'textlist-heading' : ''}
      className="max-w-screen-xxl mx-auto pr-3 md:pr-6 lg:pr-12 provider-text-list"
    >
      {(props?.fields?.subHeading?.value !== '' || isEditing) &&
        (() => {
          const HeadingTag = props?.isSidenavPart ? 'h3' : 'h2';
          return (
            <HeadingTag
              id="textlist-heading"
              className="font-centuryGothic text-22 leading-31 font-bold text-theme-text mb-8"
            >
              <PlainTextWrapper field={props?.fields?.subHeading} />
            </HeadingTag>
          );
        })()}

      {!isRichTextField ? (
        <ul className="space-y-8 list-none m-0 p-0" aria-live="polite">
          {textList.map((line: string, index: number) => (
            <RichTextWrapper
              tag="li"
              key={`line-${index}`}
              field={{ value: line }}
              className="text-base leading-28 text-theme-text"
            />
          ))}
        </ul>
      ) : (
        <RichTextWrapper
          tag="div"
          field={(rawFields[sitecoreFieldKey] as Field<string>) ?? {}}
          className="text-base leading-28 text-theme-text space-y-8 m-0 p-0"
        />
      )}
    </section>
  );
};

export const Default = withStandardComponentWrapper(TextList);
